import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, BellRing, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { ASSET_TYPES, US_STATES, scoreColor, fmtCurrency } from "@/lib/format";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    api.get("/alerts").then((r) => setAlerts(r.data));
    api.get("/alerts/matches").then((r) => setMatches(r.data));
  };

  useEffect(load, []);

  const del = async (alid) => {
    await api.delete(`/alerts/${alid}`);
    toast.success("Alert removed");
    load();
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Real-time Alerts</div>
          <h1 className="font-heading text-4xl tracking-tight">Alert Center</h1>
          <p className="text-sm text-slate-400 mt-1">Custom criteria for seller financing, master lease, distress & more</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-alert-btn" className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> New Alert
        </button>
      </header>

      {alerts.length === 0 ? (
        <div className="ae-card p-12 text-center">
          <BellRing className="h-8 w-8 mx-auto text-slate-700 mb-2" />
          <div className="text-slate-400">No alerts configured yet.</div>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm mt-4 inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Create your first alert
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {matches.map(({ alert, matches: ms, unreachable_min_score, score_ceiling }) => (
            <div key={alert.alid} className="ae-card p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading text-lg" data-testid={`alert-name-${alert.alid}`}>{alert.name}</div>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2">
                    {alert.asset_types?.length > 0 && <Chip>{alert.asset_types.join(", ")}</Chip>}
                    {alert.states?.length > 0 && <Chip>{alert.states.join(", ")}</Chip>}
                    {alert.min_score > 0 && <Chip>Score ≥ {alert.min_score}</Chip>}
                    {alert.seller_financing_only && <Chip>Seller Financing</Chip>}
                    {alert.master_lease_only && <Chip>Master Lease</Chip>}
                    {alert.distress_only && <Chip>Distress</Chip>}
                    <Chip>Frequency: {alert.frequency}</Chip>
                  </div>
                </div>
                <button onClick={() => del(alert.alid)} data-testid={`delete-alert-${alert.alid}`} className="text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-sm">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {unreachable_min_score && (
                <div data-testid={`alert-unreachable-${alert.alid}`} className="mt-4 flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    This alert will never fire. Its Min Score of <span className="font-semibold">{alert.min_score}</span> is above the highest score any current opportunity reaches (<span className="font-semibold">{score_ceiling}</span>). Lower the Min Score to start receiving matches.
                  </span>
                </div>
              )}
              <div className="mt-4 border-t border-white/8 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">{ms.length} matching opportunities</div>
                {ms.length === 0 ? (
                  <div className="text-sm text-slate-500">No matches yet. We will notify you when one appears.</div>
                ) : (
                  <div className="space-y-2">
                    {ms.slice(0, 5).map((m) => (
                      <a key={m.oid} href={`/app/opportunities/${m.oid}`} className="flex items-center justify-between text-sm py-1.5 hover:bg-white/[0.03] px-2 -mx-2 rounded-sm">
                        <div className="flex items-center gap-3">
                          <div className={`font-mono text-lg font-bold tabular-nums ${scoreColor(m.ai_score?.opportunity_score || 0)}`}>{m.ai_score?.opportunity_score || 0}</div>
                          <div>
                            <div className="text-slate-200">{m.title}</div>
                            <div className="text-[11px] text-slate-500">{m.city}, {m.state} · {fmtCurrency(m.asking_price)}</div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateAlertDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function Chip({ children }) {
  return <span className="px-2 py-0.5 bg-white/5 border border-white/10 rounded-sm text-[11px]">{children}</span>;
}

function CreateAlertDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "Motivated Sellers",
    asset_types: [],
    states: [],
    min_score: 70,
    seller_financing_only: false,
    master_lease_only: false,
    distress_only: false,
    max_price: "",
    frequency: "instant",
    slack_webhook: "",
    teams_webhook: "",
    email_enabled: true,
  });
  const [busy, setBusy] = useState(false);
  const [scoreCeiling, setScoreCeiling] = useState(null);

  useEffect(() => {
    api.get("/alerts/score-ceiling").then((r) => setScoreCeiling(r.data?.score_ceiling ?? null)).catch(() => {});
  }, []);
  const minScoreUnreachable = scoreCeiling !== null && form.min_score > scoreCeiling;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/alerts", {
        ...form,
        max_price: form.max_price ? parseFloat(form.max_price) : null,
      });
      toast.success("Alert created");
      onCreated();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key, val) => {
    const arr = form[key];
    setForm({ ...form, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6 overflow-y-auto">
      <form onSubmit={submit} className="ae-card p-6 max-w-lg w-full" data-testid="alert-dialog">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400">New Alert</div>
        <h2 className="font-heading text-2xl tracking-tight mt-1">Custom criteria</h2>

        <div className="space-y-3 mt-5 text-sm">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Alert name</label>
            <input data-testid="alert-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Asset types</label>
            <div className="flex gap-2 flex-wrap">
              {ASSET_TYPES.map((a) => (
                <button key={a.value} type="button" onClick={() => toggle("asset_types", a.value)} className={`text-xs px-2 py-1 rounded-sm border ${form.asset_types.includes(a.value) ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/50" : "border-white/10 text-slate-400"}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">States (optional)</label>
            <div className="flex gap-1 flex-wrap max-h-32 overflow-y-auto">
              {US_STATES.map((s) => (
                <button key={s} type="button" onClick={() => toggle("states", s)} className={`text-[11px] font-mono px-1.5 py-0.5 rounded-sm border ${form.states.includes(s) ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/50" : "border-white/10 text-slate-500"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Min Score: {form.min_score}</label>
              <input data-testid="alert-min-score" type="range" min="0" max="100" step="5" value={form.min_score} onChange={(e) => setForm({ ...form, min_score: +e.target.value })} className="w-full accent-emerald-500" />
              {minScoreUnreachable && (
                <div data-testid="alert-min-score-warning" className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-400">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>No opportunity currently scores above {scoreCeiling}. This alert would match nothing until scores rise.</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Max price</label>
              <input type="number" value={form.max_price} onChange={(e) => setForm({ ...form, max_price: e.target.value })} className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 font-mono" />
            </div>
          </div>

          <div className="flex gap-3 text-xs">
            <label className="inline-flex items-center gap-1.5"><input data-testid="alert-sf" type="checkbox" checked={form.seller_financing_only} onChange={(e) => setForm({ ...form, seller_financing_only: e.target.checked })} className="accent-emerald-500" /> Seller Financing</label>
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.master_lease_only} onChange={(e) => setForm({ ...form, master_lease_only: e.target.checked })} className="accent-emerald-500" /> Master Lease</label>
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.distress_only} onChange={(e) => setForm({ ...form, distress_only: e.target.checked })} className="accent-emerald-500" /> Distress</label>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Frequency</label>
            <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2">
              <option value="instant">Instant</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          <div className="pt-3 border-t border-white/8 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Delivery channels</div>
            <label className="flex items-center gap-2 text-xs">
              <input data-testid="alert-email-enabled" type="checkbox" checked={form.email_enabled} onChange={(e) => setForm({ ...form, email_enabled: e.target.checked })} className="accent-emerald-500" />
              Email digest (default)
            </label>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Slack webhook URL (optional)</label>
              <input data-testid="alert-slack" placeholder="https://hooks.slack.com/services/…" value={form.slack_webhook} onChange={(e) => setForm({ ...form, slack_webhook: e.target.value })} className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Microsoft Teams webhook URL (optional)</label>
              <input data-testid="alert-teams" placeholder="https://outlook.office.com/webhook/…" value={form.teams_webhook} onChange={(e) => setForm({ ...form, teams_webhook: e.target.value })} className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="alert-submit" className="btn-primary text-sm">Create</button>
        </div>
      </form>
    </div>
  );
}
